(function() {
  if (!document.body || !document.body.classList.contains('template-index')) {
    return;
  }

  if (document.body.dataset.disableAnimations === 'true') {
    return;
  }

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  const sectionSelector = '#MainContent > .shopify-section';
  const excludedSectionSelectors = [
    '.index-section--hero',
    '.custom-logo-marquee',
    '.custom-scrolling-text',
    '[data-section-type="slideshow-section"]',
    '.footer-promotions'
  ];
  const revealItemSelectors = [
    '.section-header',
    '.logo-bar__item',
    '.flex-grid__item',
    '.custom-popular-categories-scroll__slide',
    '.text-with-icons__block',
    '.text-with-icons__button',
    '.newsletter-section__content > .theme-block',
    '.newsletter-section__image'
  ];
  const initialRevealDelay = 180;

  let observer;

  const isVisibleElement = (element) => element && element.getClientRects().length > 0;

  const isExcludedSection = (section) => {
    if (section.hasAttribute('data-home-image-zoom-target')) {
      return true;
    }

    return excludedSectionSelectors.some((selector) => section.querySelector(selector));
  };

  const getZoomTargets = (section) => {
    if (section.querySelector('.promo-grid')) {
      return Array.from(section.querySelectorAll('.promo-grid__bg image-element')).filter(isVisibleElement);
    }

    if (section.matches('.footer-promotions') || section.querySelector('.footer-promotions')) {
      return Array.from(section.querySelectorAll('.footer-promotions .article__grid-image image-element, .article__grid-image image-element')).filter(isVisibleElement);
    }

    return [];
  };

  const clearRevealHooks = (section) => {
    section.removeAttribute('data-home-reveal-section');
    delete section.dataset.homeRevealState;
    delete section.dataset.homeRevealPrepared;

    section.querySelectorAll('[data-home-reveal-item]').forEach((item) => {
      item.removeAttribute('data-home-reveal-item');
      item.style.removeProperty('--home-reveal-order');
    });
  };

  const markImageZoomVisible = (section) => {
    section.dataset.homeImageZoomState = 'visible';

    if (observer) {
      observer.unobserve(section);
    }
  };

  const sortItemsByPosition = (items) => {
    return items
      .map((item) => ({
        item,
        rect: item.getBoundingClientRect()
      }))
      .sort((a, b) => {
        const topDiff = a.rect.top - b.rect.top;

        if (Math.abs(topDiff) > 8) {
          return topDiff;
        }

        return a.rect.left - b.rect.left;
      })
      .map((entry) => entry.item);
  };

  const collectRevealItems = (section) => {
    const items = revealItemSelectors.reduce((accumulator, selector) => {
      return accumulator.concat(Array.from(section.querySelectorAll(selector)));
    }, []);
    const uniqueItems = Array.from(new Set(items)).filter(isVisibleElement);

    return sortItemsByPosition(uniqueItems);
  };

  const markVisible = (section) => {
    section.dataset.homeRevealState = 'visible';

    if (observer) {
      observer.unobserve(section);
    }
  };

  const revealWithDelay = (section, delay = 0) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          markVisible(section);
        }, delay);
      });
    });
  };

  const zoomWithDelay = (section, delay = 0) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          markImageZoomVisible(section);
        }, delay);
      });
    });
  };

  const prepareSection = (section) => {
    if (!section || section.dataset.homeRevealPrepared === 'true' || isExcludedSection(section)) {
      return;
    }

    const items = collectRevealItems(section);

    if (!items.length) {
      return;
    }

    section.setAttribute('data-home-reveal-section', '');
    section.dataset.homeRevealPrepared = 'true';
    section.dataset.homeRevealState = 'pending';

    items.forEach((item, index) => {
      item.setAttribute('data-home-reveal-item', '');
      item.style.setProperty('--home-reveal-order', index);
    });

    const sectionTop = section.getBoundingClientRect().top;
    const viewportThreshold = window.innerHeight * 0.45;

    if (sectionTop <= viewportThreshold) {
      revealWithDelay(section, initialRevealDelay);
      return;
    }

    observer.observe(section);
  };

  const prepareImageZoomSection = (section, options = {}) => {
    if (!section || section.dataset.homeImageZoomPrepared === 'true') {
      return;
    }

    const images = getZoomTargets(section);

    if (!images.length) {
      return;
    }

    clearRevealHooks(section);
    section.dataset.homeImageZoomPrepared = 'true';
    section.setAttribute('data-home-image-zoom', '');
    section.dataset.homeImageZoomState = 'pending';

    images.forEach((image) => {
      image.setAttribute('data-home-image-zoom-item', '');
    });

    if (options.immediate) {
      zoomWithDelay(section, initialRevealDelay);
      return;
    }

    observer.observe(section);
  };

  const initHomepageReveal = () => {
    const sections = document.querySelectorAll(sectionSelector);
    const firstPromoGrid = Array.from(sections).find((section) => section.querySelector('.promo-grid'));
    const footerPromotions = document.querySelector('.shopify-section-group-footer-group .footer-promotions');

    if (!sections.length) {
      return;
    }

    document.body.classList.add('js-home-reveal');

    if (firstPromoGrid) {
      firstPromoGrid.setAttribute('data-home-image-zoom-target', '');
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          window.setTimeout(() => {
            document.documentElement.classList.remove('first-promo-zoom-pending');
            document.documentElement.classList.add('first-promo-zoom-visible');
          }, initialRevealDelay);
        });
      });
    }

    if (footerPromotions) {
      prepareImageZoomSection(footerPromotions);
    }

    sections.forEach(prepareSection);
  };

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (entry.target.hasAttribute('data-home-image-zoom')) {
            zoomWithDelay(entry.target);
            return;
          }

          revealWithDelay(entry.target);
        }
      });
    },
    {
      rootMargin: '0px 0px -6% 0px',
      threshold: 0.2
    }
  );

  initHomepageReveal();

  document.addEventListener('page:loaded', initHomepageReveal);
  document.addEventListener('shopify:section:load', (event) => {
    prepareSection(event.target);

    if (event.target.hasAttribute('data-home-image-zoom-target') || event.target.matches('.footer-promotions') || event.target.querySelector('.footer-promotions')) {
      prepareImageZoomSection(event.target);
    }
  });
})();
